import React from "react";
import overpayLogo from "components/Icon/icons/overpay.svg";
import tetherLogo from "components/UsdtDepositForm/assets/TetherUSDTLogo.png";
import { IconType } from "enums/Icon";
import { useTranslation } from "react-i18next";
import {
  getPaymentOptionLabel,
  USDT_PAYMENT_OPTION,
  type PaymentOption,
} from "utils/paymentMethods";
import payPalLogo from "./assets/PayPal.svg";
import {
  PaymentMethodButton,
  PaymentMethodLogo,
  PaymentMethodRadio,
  PaymentMethodTitle,
  PaymentOptions,
} from "./PaymentMethodSelector.styles";

interface PaymentMethodSelectorProps {
  paymentMethods: PaymentOption[];
  selectedPaymentMethod?: PaymentOption;
  onSelect: (method: PaymentOption) => void;
}

const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = React.memo(
  ({ paymentMethods, selectedPaymentMethod, onSelect }) => {
    const { t } = useTranslation("common");

    return (
      <PaymentOptions role="radiogroup" aria-label={t("parcels.paymentMethod")}>
        {paymentMethods.map((method) => {
          const isSelected = selectedPaymentMethod === method;
          const label = getPaymentOptionLabel(t, method);

          // No `default`: a new PaymentOption member must get a case here.
          let methodPresentation: React.ReactNode;
          switch (method) {
            case "paypal":
              methodPresentation = (
                <PaymentMethodLogo src={payPalLogo} alt={label} />
              );
              break;
            case "overpay":
              methodPresentation = (
                <PaymentMethodLogo src={overpayLogo} alt={label} />
              );
              break;
            case USDT_PAYMENT_OPTION:
              methodPresentation = (
                <>
                  <PaymentMethodLogo src={tetherLogo} alt="" />
                  <PaymentMethodTitle>{label}</PaymentMethodTitle>
                </>
              );
              break;
            case "credit_card":
            case "stripe":
              methodPresentation = (
                <PaymentMethodTitle>{label}</PaymentMethodTitle>
              );
              break;
          }

          return (
            <PaymentMethodButton
              key={method}
              type="button"
              onClick={() => onSelect(method)}
              role="radio"
              aria-checked={isSelected}
            >
              <PaymentMethodRadio
                type={
                  isSelected
                    ? IconType.RadioButtonFill
                    : IconType.RadioButtonLight
                }
                $selected={isSelected}
              />
              {methodPresentation}
            </PaymentMethodButton>
          );
        })}
      </PaymentOptions>
    );
  },
);

export default PaymentMethodSelector;
